import type { Span } from '@opentelemetry/api';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';

import staticConfig from './staticConfig';

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const originOf = (url: string): string => new URL(url, window.location.origin).origin;

// Cloud Trace names a trace after its root span, and the fetch instrumentation names every
// span after the HTTP method alone — without this every frontend trace is listed as "POST".
const nameAfterGraphqlOperation = (span: Span, request: Request | RequestInit): void => {
  if (typeof request.body !== 'string') {
    return;
  }

  try {
    const { operationName, query } = JSON.parse(request.body) as { operationName?: string; query?: string };

    if (!operationName) {
      return;
    }

    const operationType: string = /^\s*(query|mutation|subscription)\b/.exec(query ?? '')?.[1] ?? 'query';

    span.updateName(`${operationType} ${operationName}`);
    span.setAttribute('graphql.operation.name', operationName);
    span.setAttribute('graphql.operation.type', operationType);
  } catch {
    // A non-JSON body is not a GraphQL operation; the default span name stands.
  }
};

// Spans are exported to a same-origin path that Istio routes to the collector, so the
// browser never needs CORS for telemetry and ad blockers have no third-party host to match.
export const initTracing = (): void => {
  const { applicationVersion, environmentName, otlpTracesUrl, unproxiedGraphqlUrl } = staticConfig;

  // Unset in every environment where tracing is not rolled out yet — this is the kill switch.
  if (!otlpTracesUrl) {
    return;
  }

  const provider: WebTracerProvider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: 'frontend',
      [ATTR_SERVICE_VERSION]: applicationVersion,
      'deployment.environment': environmentName
    }),
    spanProcessors: [new BatchSpanProcessor(new OTLPTraceExporter({ url: otlpTracesUrl }))]
  });

  provider.register();

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        // Backstop only: the exporter already calls the unpatched fetch, so its own requests
        // do not become spans. Without either guard, exporting would slowly feed itself.
        ignoreUrls: [new RegExp(escapeForRegExp(otlpTracesUrl))],
        // GraphQL is on a different origin, so `traceparent` is only attached where the
        // server is known to accept it on a CORS preflight.
        propagateTraceHeaderCorsUrls: [new RegExp(`^${escapeForRegExp(originOf(unproxiedGraphqlUrl))}`)],
        requestHook: nameAfterGraphqlOperation
      })
    ]
  });

  // A tab closing or backgrounding would otherwise discard whatever the batch processor is holding.
  document.addEventListener('visibilitychange', (): void => {
    if (document.visibilityState === 'hidden') {
      void provider.forceFlush();
    }
  });
};
